import {
  ClaimMessageDTO,
  IncidentDTO,
  SearchMetadataDto
} from "@/model/data.model";
import store from "@/vuex/store";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

export class ClaimService {
  static getClaims(filter: any, page = 0, pageSize = 25, sortBy?: string) {
    const params: any = {
      page,
      pageSize
    };

    if (sortBy) {
      params.sortBy = sortBy;
    }

    const config: AxiosRequestConfig = {
      method: "POST",
      url: `${store.getters.BASE_API_URL}/claim/search`,
      params,
      data: filter
    };

    return axios
      .request(config)
      .then(res => res.data)
      .catch(err => {
        console.error(err);
        return Promise.reject(err);
      });
  }

  static getClaimMetadata(): Promise<SearchMetadataDto> {
    const config: AxiosRequestConfig = {
      method: "GET",
      url: `${store.getters.BASE_API_URL}/claim/search/metadata/mol-claim-search`
    };
    return axios
      .request(config)
      .then(res => res.data)
      .catch(err => {
        console.error(err);
        return Promise.reject(err);
      });
  }

  static getClaimStatusList() {
    const config = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/claim/status"
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  static getClaimCategorization(): Promise<any> {
    const config = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/claim/categorization"
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  static getClaimPriorities() {
    const config = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/claim/priority"
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  static getClaimErrorSubTypes(errorType: string) {
    const config = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/claim/error-subtypes",
      params: {
        errorType
      }
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  static getClaimErrorTypes() {
    const config = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/claim/error-types"
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  static openClaim(incidentId: string, claim: any) {
    const config = {
      method: "POST",
      data: {
        incidentId,
        claim
      },
      url: store.getters.BASE_API_URL + "/claim/claims"
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  static openIncident(
    shipmentId: string,
    categorization: string,
    description: string,
    files?: File[]
  ) {
    const formData = new FormData();

    formData.append("categorization", categorization);
    formData.append("description", description);

    if (files && files.length > 0) {
      files.forEach(file => {
        formData.append("files", file);
      });
    }

    const config = {
      method: "POST",
      url:
        store.getters.BASE_API_URL +
        `/claim/incidents?shipmentId=${shipmentId}`,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    } as AxiosRequestConfig;

    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => res.data)
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  updateIncidentInfo(id: string, item: any) {
    const config = {
      method: "PUT",
      data: item,
      url: store.getters.BASE_API_URL + `/claim/incidents/mol/${id}`
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  getIncidentData(id: string): Promise<IncidentDTO> {
    const config = {
      method: "GET",
      url: store.getters.BASE_API_URL + "/claim/incidents",
      params: {
        incidentId: id
      }
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  getClaimAttachmentList(incidentId: string, paths: string[]) {
    const config = {
      method: "POST",
      url: store.getters.BASE_API_URL + `/claim/${incidentId}/get-attachments`,
      data: {
        paths: paths
      }
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  uploadAttachment(formData: FormData, incidentId: string) {
    return axios
      .post(
        `${store.getters.BASE_API_URL}/claim/${incidentId}/add-attachments`,
        formData
      )
      .then(req => req.data as IncidentDTO)
      .catch(err => {
        console.error(err);
        return Promise.reject(err);
      });
  }

  deleteAttachments(incidentId: string, paths: string[]) {
    return axios
      .delete(
        `${store.getters.BASE_API_URL}/claim/${incidentId}/delete-attachments`,
        {
          data: {
            paths
          }
        }
      )
      .then(req => req.data.status === 200)
      .catch(err => {
        console.error(err);
        return Promise.reject(err);
      });
  }

  createClaimMessage(incidentId: string, message: string) {
    const config = {
      method: "POST",
      data: {
        message
      },
      url: store.getters.BASE_API_URL + `/claim/${incidentId}/claim-message`
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  updateClaimMessage(data: ClaimMessageDTO, incidentId: string) {
    const config = {
      method: "PUT",
      data: {
        message: data.text
      },
      url:
        store.getters.BASE_API_URL +
        `/claim/${incidentId}/claim-message/${data.messageId}`
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  deleteClaimMessage(messageId: string, incidentId: string) {
    const config = {
      method: "DELETE",
      url:
        store.getters.BASE_API_URL +
        `/claim/${incidentId}/claim-message/${messageId}`
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }

  deleteIncident(incidentIds: string[]) {
    const config = {
      method: "DELETE",
      url: store.getters.BASE_API_URL + `/claim/incidents`,
      data: incidentIds
    } as AxiosRequestConfig;
    return axios
      .request(config)
      .then((res: AxiosResponse<any>) => {
        return res.data;
      })
      .catch(err => {
        console.error("Response failed with message: " + err.message);
        return Promise.reject(err);
      });
  }
}
